import React from 'react';

const DeckAnalysis = ({ stats, deck }) => {
  const {
    totalCards,
    unitCount,
    spellCount,
    gearCount,
    costDistribution
  } = stats;

  // Calculate card type distribution percentages
  const totalMainCards = unitCount + spellCount + gearCount;
  const unitPercentage = totalMainCards > 0 ? Math.round((unitCount / totalMainCards) * 100) : 0;
  const spellPercentage = totalMainCards > 0 ? Math.round((spellCount / totalMainCards) * 100) : 0;
  const gearPercentage = totalMainCards > 0 ? Math.round((gearCount / totalMainCards) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Card Type Distribution */}
      <div className="bg-background/30 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">Card Type Distribution</h4>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              Units
            </span>
            <span>{unitCount} ({unitPercentage}%)</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div 
              className="bg-blue-500 h-3 rounded-full" 
              style={{ width: `${unitPercentage}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              Spells
            </span>
            <span>{spellCount} ({spellPercentage}%)</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div 
              className="bg-green-500 h-3 rounded-full" 
              style={{ width: `${spellPercentage}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded"></div>
              Gear
            </span>
            <span>{gearCount} ({gearPercentage}%)</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div 
              className="bg-purple-500 h-3 rounded-full" 
              style={{ width: `${gearPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Energy Cost Distribution */}
      {costDistribution && Object.keys(costDistribution).length > 0 && (
        <div className="bg-background/30 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">Energy Cost Distribution</h4>
          <div className="space-y-2">
            {Object.entries(costDistribution)
              .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
              .map(([cost, count]) => {
                const costNum = parseInt(cost, 10);
                const percentage = totalMainCards > 0 ? Math.round((count / totalMainCards) * 100) : 0;
                const maxBarWidth = Math.min(100, percentage * 3);

                return (
                  <div key={cost} className="flex items-center gap-2">
                    <div className="w-10 text-sm font-medium">{costNum === 0 ? '0' : costNum}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-full bg-muted rounded-full h-6 relative overflow-hidden">
                          <div
                            className={`h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white transition-all ${
                              costNum === 0 ? 'bg-green-500' :
                              costNum <= 2 ? 'bg-blue-500' :
                              costNum <= 4 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${maxBarWidth}%` }}
                          >
                            {count > 0 && maxBarWidth > 15 ? count : ''}
                          </div>
                        </div>
                        <div className="w-12 text-xs text-muted-foreground text-right">{percentage}%</div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>0 Energy</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span>1-2 Energy</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>3-4 Energy</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>5+ Energy</span>
            </div>
          </div>
        </div>
      )}

      {/* Strategic Insights */}
      <div className="bg-background/30 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">Strategic Insights</h4>
        <ul className="space-y-2 text-sm">
          {unitPercentage < 30 && (
            <li className="flex items-start gap-2">
              <span className="text-yellow-500">⚠️</span>
              <span>Your deck has a low unit count ({unitPercentage}%). Consider adding more units for board presence.</span>
            </li>
          )}
          {unitPercentage > 60 && (
            <li className="flex items-start gap-2">
              <span className="text-yellow-500">⚠️</span>
              <span>Your deck has a high unit count ({unitPercentage}%). Make sure you have enough spells/gear for support.</span>
            </li>
          )}
          {gearPercentage < 10 && totalMainCards > 0 && (
            <li className="flex items-start gap-2">
              <span className="text-blue-500">ℹ️</span>
              <span>Your deck has few gear cards ({gearPercentage}%). Gear can provide ongoing benefits.</span>
            </li>
          )}
          {Object.keys(costDistribution).length > 0 && (
            <>
              {costDistribution[0] && costDistribution[0] / totalMainCards > 0.2 && (
                <li className="flex items-start gap-2">
                  <span className="text-blue-500">ℹ️</span>
                  <span>You have many 0 Energy cards ({Math.round((costDistribution[0] / totalMainCards) * 100)}%). These provide immediate value.</span>
                </li>
              )}
              {costDistribution[6] && costDistribution[6] / totalMainCards > 0.15 && (
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500">⚠️</span>
                  <span>You have many high Energy cards (6+). Consider adding more low Energy options for early game.</span>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default DeckAnalysis;